import { useState, useRef, useEffect } from "react";
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiMinimize, FiSkipForward, FiSkipBack } from "react-icons/fi";

const CustomVideoPlayer = ({
  src,
  isDark,
  className = "",
  initialTime = 0,
  onTimeUpdate,
  onEnded,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [error, setError] = useState(null);
  const controlsTimeoutRef = useRef(null);

  useEffect(() => {
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e) => {
      console.error("Video error:", e);
      setError("Failed to load video. Please try again.");
    };
    const handleLoadedData = () => {
      setError(null);
      if (initialTime > 0 && video.duration && initialTime < video.duration - 2) {
        video.currentTime = initialTime;
      }
    };
    const handleTimeUpdateWithCallback = () => {
      updateTime();
      if (onTimeUpdate) onTimeUpdate(video.currentTime, video.duration);
    };
    const handleEndedWithCallback = () => {
      handleEnded();
      if (onEnded) onEnded();
    };

    video.addEventListener("timeupdate", handleTimeUpdateWithCallback);
    video.addEventListener("loadedmetadata", updateDuration);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEndedWithCallback);
    video.addEventListener("error", handleError);
    video.addEventListener("loadeddata", handleLoadedData);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdateWithCallback);
      video.removeEventListener("loadedmetadata", updateDuration);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEndedWithCallback);
      video.removeEventListener("error", handleError);
      video.removeEventListener("loadeddata", handleLoadedData);
    };
  }, [src, initialTime, onTimeUpdate, onEnded]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video || !src || error) return;
    if (video.paused) {
      try {
        await video.play();
      } catch (e) {
        setError("Failed to load video. Please try again.");
      }
    } else {
      video.pause();
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * duration;
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newVolume = Math.max(0, Math.min(1, pos));
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video.muted || volume === 0) {
      video.muted = false;
      video.volume = volume || 0.5;
      setIsMuted(false);
      setVolume(video.volume);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(err => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const skip = (seconds) => {
    const video = videoRef.current;
    video.currentTime = Math.max(0, Math.min(duration, video.currentTime + seconds));
  };

  const handleMouseMove = () => {
    setIsHovering(true);
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (!isPlaying) return; // Keep controls visible if paused
      setShowControls(false);
    }, 3000);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (isPlaying) {
      setShowControls(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const volumeProgress = volume * 100;

  return (
    <div
      ref={containerRef}
      className="relative mx-auto inline-block max-w-full rounded-radius-sm overflow-hidden bg-black"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={toggleFullscreen}
    >
      <video
        ref={videoRef}
        src={src}
        className={`block h-auto w-auto max-w-full object-contain ${className}`}
        style={{ objectFit: "contain" }}
        playsInline
        onClick={togglePlay}
        preload="metadata"
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white p-4">
          <p className="text-center">{error}</p>
        </div>
      )}

      {/* Controls Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${
          showControls || isHovering ? "opacity-100" : "opacity-0"
        } pointer-events-none`}
      >
        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 p-4 flex justify-end pointer-events-auto">
          <button
            onClick={toggleFullscreen}
            className={`p-2 rounded-radius-xs backdrop-blur-sm transition ${
              isDark
                ? "bg-white/10 hover:bg-white/20 text-white"
                : "bg-black/30 hover:bg-black/50 text-white"
            }`}
          >
            {isFullscreen ? <FiMinimize className="h-5 w-5" /> : <FiMaximize className="h-5 w-5" />}
          </button>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3 pointer-events-auto">
          {/* Progress Bar */}
          <div
            className="relative h-1.5 bg-white/20 rounded-full cursor-pointer group"
            onClick={handleSeek}
          >
            <div
              className="absolute h-full bg-accent-default rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 h-3 w-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-all -translate-x-1/2"
              style={{ left: `${progress}%` }}
            />
          </div>

          {/* Control Buttons */}
          <div className="flex items-center gap-3">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className={`p-2 rounded-radius-xs backdrop-blur-sm transition ${
                isDark
                  ? "bg-white/10 hover:bg-white/20 text-white"
                  : "bg-black/30 hover:bg-black/50 text-white"
              }`}
            >
              {isPlaying ? <FiPause className="h-5 w-5" /> : <FiPlay className="h-5 w-5" />}
            </button>

            {/* Skip Back */}
            <button
              onClick={() => skip(-10)}
              className={`p-2 rounded-radius-xs backdrop-blur-sm transition ${
                isDark
                  ? "bg-white/10 hover:bg-white/20 text-white"
                  : "bg-black/30 hover:bg-black/50 text-white"
              }`}
            >
              <FiSkipBack className="h-4 w-4" />
            </button>

            {/* Skip Forward */}
            <button
              onClick={() => skip(10)}
              className={`p-2 rounded-radius-xs backdrop-blur-sm transition ${
                isDark
                  ? "bg-white/10 hover:bg-white/20 text-white"
                  : "bg-black/30 hover:bg-black/50 text-white"
              }`}
            >
              <FiSkipForward className="h-4 w-4" />
            </button>

            {/* Volume */}
            <div className="flex items-center gap-2 flex-1">
              <button
                onClick={toggleMute}
                className={`p-2 rounded-radius-xs backdrop-blur-sm transition ${
                  isDark
                    ? "bg-white/10 hover:bg-white/20 text-white"
                    : "bg-black/30 hover:bg-black/50 text-white"
                }`}
              >
                {isMuted || volume === 0 ? (
                  <FiVolumeX className="h-4 w-4" />
                ) : (
                  <FiVolume2 className="h-4 w-4" />
                )}
              </button>
              <div
                className="relative h-1.5 flex-1 max-w-24 bg-white/20 rounded-full cursor-pointer group"
                onClick={handleVolumeChange}
              >
                <div
                  className="absolute h-full bg-accent-default rounded-full transition-all"
                  style={{ width: `${volumeProgress}%` }}
                />
              </div>
            </div>

            {/* Time */}
            <div className={`text-footnote font-medium ${
              isDark ? "text-white/90" : "text-white"
            }`}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;

